import {combine, restore, Store} from 'effector';
import {createGate} from 'effector-react';

import {
    deleteInvoice,
    fetchStorageLocation,
    fetchTinLegalName,
    updateInvoiceBody,
    updateInvoiceStatus,
} from 'api/documents/documents';
import {DocumentStatus} from 'core/dictionaries';
import {rootDomain} from 'core/rootDomain';
import {permissions$} from 'models/auth';
import {UserPermissionValue} from 'types';
import {ExistingInvoiceState, NewInvoiceIdState} from 'types/invoices';

import {checkPermission} from './helper';

const domain = rootDomain.createDomain('InvoiceCommon');

export const CreateInvoiceGate = createGate('CreateInvoiceGate');
export const GeneralInfoGate = createGate('GeneralInfo');
export const DocumentsGate = createGate('Documents');
export const InvoiceGeneratingGate = createGate('InvoiceGenerating');
export const TaxesAndTotalGate = createGate('Taxes');

export const $newInvoiceIsDone = domain.createStore<boolean>(false).reset(CreateInvoiceGate.close);
export const $fetchDataFxFailed = domain.createStore<boolean>(false).reset(CreateInvoiceGate.close);
export const $invoiceIsRemoved = domain.createStore<boolean>(false).reset(CreateInvoiceGate.close);

export const setShowDeleteInvoiceModal = domain.createEvent<boolean>();
export const $showDeleteInvoiceModal = restore(setShowDeleteInvoiceModal, false).reset(
    CreateInvoiceGate.close,
);

export type InvoiceStatusInfo = {
    side: 'OUTGOING' | 'INCOMING' | '';
    status: DocumentStatus | null;
};
export const $invoiceStatusInfo = domain
    .createStore<InvoiceStatusInfo | null>({
        side: 'OUTGOING',
        status: null,
    })
    .reset(CreateInvoiceGate.close);

export const setNewInvoiceIsDone = domain.createEvent<boolean>();
export const setInvoiceState = domain.createEvent<
    ExistingInvoiceState | NewInvoiceIdState | null
>();

export const $invoiceState = restore<ExistingInvoiceState | NewInvoiceIdState | null>(
    setInvoiceState,
    null,
).reset(CreateInvoiceGate.close);

export const setShowNoFirsModal = domain.createEvent<boolean>();
export const $showNoFirsModal = restore<boolean>(setShowNoFirsModal, false).reset(
    CreateInvoiceGate.close,
);

export const fetchTinLegalNameFx = domain.createEffect({
    name: 'fetchTinLegalNameFx',
    handler: fetchTinLegalName,
});

export const fetchStorageLocationFx = domain.createEffect({
    name: 'fetchStorageLocationFx',
    handler: fetchStorageLocation,
});

export const deleteInvoiceFx = domain.createEffect({
    name: 'deleteInvoiceFx',
    handler: deleteInvoice,
});

export const updateInvoiceStatusFx = domain.createEffect({
    name: 'updateInvoiceStatusFx',
    handler: updateInvoiceStatus,
});

export const updateInvoiceBodyFx = domain.createEffect({
    name: 'updateInvoiceBodyFx',
    handler: updateInvoiceBody,
});

export const fetchOptionFx = domain.createEffect(
    async (): Promise<Record<string, string>[]> => {
        await new Promise((res) => {
            setTimeout(() => {
                res('');
            }, 0);
        });
        return [
            {label: 'Boali Distribution Center 1', value: '1'},
            {label: 'Boali Distribution Center 2', value: '2'},
            {label: 'Boali Distribution Center 3', value: '3'},
        ];
    },
);
export const fetchCompanyNameFx = domain.createEffect(
    async (): Promise<Record<string, string>[]> => {
        await new Promise((res) => {
            setTimeout(() => {
                res('');
            }, 0);
        });
        return [
            {label: 'Storage Name 1', value: 'Storage Name 1'},
            {label: 'Storage Name 2', value: 'Storage Name 2'},
            {label: 'Storage Name 3', value: 'Storage Name 3'},
        ];
    },
);
export const fetchTaxCategodyFx = domain.createEffect(
    async (): Promise<Record<string, string>[]> => {
        await new Promise((res) => {
            setTimeout(() => {
                res('');
            }, 0);
        });
        return [
            {label: 'Payment Mean 1', value: 'Payment Mean 1'},
            {label: 'Payment Mean 2', value: 'Payment Mean 2'},
            {label: 'Payment Mean 3', value: 'Payment Mean 3'},
        ];
    },
);
export const fetchPaymentMeansFx = domain.createEffect(
    async (): Promise<Record<string, string>[]> => {
        await new Promise((res) => {
            setTimeout(() => {
                res('');
            }, 0);
        });
        return [
            {
                value: '1',
                label: 'Instrument not defined',
                text:
                    'Not defined legally enforceable agreement between two or more parties (expressing a contractual right or a right to the payment of money).',
            },
            {
                value: '2',
                label: 'Automated clearing house credit',
                text: 'A credit transaction made through the automated clearing house system.',
            },
            {
                value: '3',
                label: 'Automated clearing house debit',
                text: 'A debit transaction made through the automated clearing house system.',
            },
            {
                value: '4',
                label: 'ACH demand debit reversal',
                text: 'A request to reverse an ACH debit transaction to a demand deposit account.',
            },
            {
                value: '5',
                label: 'ACH demand credit reversal',
                text: 'A request to reverse a credit transaction to a demand deposit account.',
            },
            {
                value: '6',
                label: 'ACH demand credit',
                text:
                    'A credit transaction made through the ACH system to a demand deposit account.',
            },
            {
                value: '7',
                label: 'ACH demand debit',
                text:
                    'A debit transaction made through the ACH system to a demand deposit account.',
            },
            {
                value: '8',
                label: 'Hold',
                text:
                    'Indicates that the bank should hold the payment for collection by the beneficiary or other instructions.',
            },
            {
                value: '9',
                label: 'National or regional clearing',
                text:
                    'Indicates that the payment should be made using the national or regional clearing.',
            },
            {
                value: '10',
                label: 'In cash',
                text:
                    'Payment by currency (including bills and coins) in circulation, including checking account deposits.',
            },
            {
                value: '11',
                label: 'ACH savings credit reversal',
                text: 'A request to reverse an ACH credit transaction to a savings account.',
            },
            {
                value: '12',
                label: 'ACH savings debit reversal',
                text: 'A request to reverse an ACH debit transaction to a savings account.',
            },
            {
                value: '13',
                label: 'ACH savings credit',
                text: 'A credit transaction made through the ACH system to a savings account.',
            },
            {
                value: '14',
                label: 'ACH savings debit',
                text: 'A debit transaction made through the ACH system to a savings account.',
            },
            {
                value: '15',
                label: 'Bookentry credit',
                text:
                    'A credit entry between two accounts at the same bank branch. Synonym: house credit.',
            },
            {
                value: '16',
                label: 'Bookentry debit',
                text:
                    'A debit entry between two accounts at the same bank branch. Synonym: house debit.',
            },
            {
                value: '17',
                label: 'ACH demand cash concentration/disbursement (CCD) credit',
                text:
                    'A credit transaction made through the ACH system to a demand deposit account using the CCD payment format.',
            },
            {
                value: '18',
                label: 'ACH demand cash concentration/disbursement (CCD) debit',
                text:
                    'A debit transaction made through the ACH system to a demand deposit account using the CCD payment format.',
            },
            {
                value: '19',
                label: 'ACH demand corporate trade payment (CTP) credit',
                text:
                    'A credit transaction made through the ACH system to a demand deposit account using the CTP payment format.',
            },
            {
                value: '20',
                label: 'Cheque',
                text:
                    'Payment by a pre-printed form on which instructions are given to an account holder (a bank or building society) to pay a stated sum to a named recipient.',
            },
            {
                value: '21',
                label: "Banker's draft",
                text: "Issue of a banker's draft in payment of the funds.",
            },
            {
                value: '22',
                label: "Certified banker's draft",
                text:
                    'Cheque drawn by a bank on itself or its agent. A person who owes money to another buys the draft from a bank for cash and hands it to the creditor who need have no fear that it might be dishonoured.',
            },
            {
                value: '23',
                label: 'Bank cheque (issued by a banking or similar establishment)',
                text:
                    'Payment by a pre-printed form, which has been completed by a financial institution, on which instructions are given to an account holder (a bank or building society) to pay a stated sum to a named recipient.',
            },
            {
                value: '24',
                label: 'Bill of exchange awaiting acceptance',
                text:
                    'Bill drawn by the creditor on the debtor but not yet accepted by the debtor.',
            },
            {
                value: '25',
                label: 'Certified cheque',
                text:
                    "Payment by a pre-printed form stamped with the paying bank's certification on which instructions are given to an account holder (a bank or building society) to pay a stated sum to a named recipient .",
            },
            {
                value: '26',
                label: 'Local cheque',
                text: 'Indicates that the cheque is given local to the recipient.',
            },
            {
                value: '27',
                label: 'ACH demand corporate trade payment (CTP) debit',
                text:
                    'A debit transaction made through the ACH system to a demand deposit account using the CTP payment format.',
            },
            {
                value: '28',
                label: 'ACH demand corporate trade exchange (CTX) credit',
                text:
                    'A credit transaction made through the ACH system to a demand deposit account using the CTX payment format.',
            },
            {
                value: '29',
                label: 'ACH demand corporate trade exchange (CTX) debit',
                text:
                    'A debit transaction made through the ACH system to a demand account using the CTX payment format.',
            },
            {
                value: '30',
                label: 'Credit transfer',
                text: 'Payment by credit movement of funds from one account to another.',
            },
            {
                value: '31',
                label: 'Debit transfer',
                text: 'Payment by debit movement of funds from one account to another.',
            },
            {
                value: '32',
                label: 'ACH demand cash concentration/disbursement plus (CCD+) credit',
                text:
                    'A credit transaction made through the ACH system to a demand deposit account using the CCD+ payment format.',
            },
            {
                value: '33',
                label: 'ACH demand cash concentration/disbursement plus (CCD+) debit',
                text:
                    'A debit transaction made through the ACH system to a demand deposit account using the CCD+ payment format.',
            },
            {
                value: '34',
                label: 'ACH prearranged payment and deposit (PPD)',
                text:
                    'A consumer credit transaction made through the ACH system to a demand deposit or savings account.',
            },
            {
                value: '35',
                label: 'ACH savings cash concentration/disbursement (CCD) credit',
                text:
                    'A credit transaction made through the ACH system to a demand deposit or savings account.',
            },
            {
                value: '36',
                label: 'ACH savings cash concentration/disbursement (CCD) debit',
                text:
                    'A debit transaction made through the ACH system to a savings account using the CCD payment format.',
            },
            {
                value: '37',
                label: 'ACH savings corporate trade payment (CTP) credit',
                text:
                    'A credit transaction made through the ACH system to a savings account using the CTP payment format.',
            },
            {
                value: '38',
                label: 'ACH savings corporate trade payment (CTP) debit',
                text:
                    'A debit transaction made through the ACH system to a savings account using the CTP payment format.',
            },
            {
                value: '39',
                label: 'ACH savings corporate trade exchange (CTX) credit',
                text:
                    'A credit transaction made through the ACH system to a savings account using the CTX payment format.',
            },
            {
                value: '40',
                label: 'ACH savings corporate trade exchange (CTX) debit',
                text:
                    'A debit transaction made through the ACH system to a savings account using the CTX payment format.',
            },
            {
                value: '41',
                label: 'ACH savings cash concentration/disbursement plus (CCD+) credit',
                text:
                    'A credit transaction made through the ACH system to a savings account using the CCD+ payment format.',
            },
            {
                value: '42',
                label: 'Payment to bank account',
                text:
                    'Payment by an arrangement for settling debts that is operated by the Post Office.',
            },
            {
                value: '43',
                label: 'ACH savings cash concentration/disbursement plus (CCD+) debit',
                text:
                    'A debit transaction made through the ACH system to a savings account using the CCD+ payment format.',
            },
            {
                value: '44',
                label: 'Accepted bill of exchange',
                text: 'Bill drawn by the creditor on the debtor and accepted by the debtor.',
            },
            {
                value: '45',
                label: 'Referenced home-banking credit transfer',
                text: 'A referenced credit transfer initiated through home- banking.',
            },
            {
                value: '46',
                label: 'Interbank debit transfer',
                text: 'A debit transfer via interbank means.',
            },
            {
                value: '47',
                label: 'Home-banking debit transfer',
                text: 'A debit transfer initiated through home-banking.',
            },
            {
                value: '48',
                label: 'Bank card',
                text: 'Payment by means of a card issued by a bank or other financial institution.',
            },
            {
                value: '49',
                label: 'Direct debit',
                text:
                    "The amount is to be, or has been, directly debited to the customer's bank account.",
            },
            {
                value: '50',
                label: 'Payment by postgiro',
                text:
                    'A method for the transmission of funds through the postal system rather than through the banking system.',
            },
            {
                value: '51',
                label:
                    'FR, norme 6 97-Telereglement CFONB (French Organisation for Banking Standards) - Option A',
                text:
                    'A French standard procedure that allows a debtor to pay an amount due to a creditor. The creditor will forward it to its bank, which will collect the money on the bank account of the debtor.',
            },
            {
                value: '52',
                label: 'Urgent commercial payment',
                text:
                    'Payment order which requires guaranteed processing by the most appropriate means to ensure it occurs on the requested execution date, provided that it is issued to the ordered bank before the agreed cut-off time.',
            },
            {
                value: '53',
                label: 'Urgent Treasury Payment',
                text:
                    'Payment order or transfer which must be executed, by the most appropriate means, as urgently as possible and before urgent commercial payments.',
            },
            {
                value: '54',
                label: 'Credit card',
                text: 'Payment made by means of credit card.',
            },
            {
                value: '55',
                label: 'Debit card',
                text: 'Payment made by means of debit card.',
            },
            {
                value: '56',
                label: 'Bankgiro',
                text: 'Payment will be, or has been, made by bankgiro.',
            },
            {
                value: '57',
                label: 'Standing agreement',
                text:
                    'The payment means have been previously agreed between seller and buyer and thus are not stated again.',
            },
            {
                value: '58',
                label: 'SEPA credit transfer',
                text: 'Credit transfer inside the Single Euro Payment Area (SEPA) system.',
            },
            {
                value: '59',
                label: 'SEPA direct debit',
                text: 'Direct debit inside the Single Euro Payment Area (SEPA) system.',
            },
            {
                value: '60',
                label: 'Promissory note',
                text:
                    'Payment by an unconditional promise in writing made by one person to another, signed by the maker, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '61',
                label: 'Promissory note signed by the debtor',
                text:
                    'Payment by an unconditional promise in writing made by the debtor to another person, signed by the debtor, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '62',
                label: 'Promissory note signed by the debtor and endorsed by a bank',
                text:
                    'Payment by an unconditional promise in writing made by the debtor to another person, signed by the debtor and endorsed by a bank, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '63',
                label: 'Promissory note signed by the debtor and endorsed by a third party',
                text:
                    'Payment by an unconditional promise in writing made by the debtor to another person, signed by the debtor and endorsed by a third party, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '64',
                label: 'Promissory note signed by a bank',
                text:
                    'Payment by an unconditional promise in writing made by the bank to another person, signed by the bank, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '65',
                label: 'Promissory note signed by a bank and endorsed by another bank',
                text:
                    'Payment by an unconditional promise in writing made by the bank to another person, signed by the bank and endorsed by another bank, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '66',
                label: 'Promissory note signed by a third party',
                text:
                    'Payment by an unconditional promise in writing made by a third party to another person, signed by the third party, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '67',
                label: 'Promissory note signed by a third party and endorsed by a bank',
                text:
                    'Payment by an unconditional promise in writing made by a third party to another person, signed by the third party and endorsed by a bank, engaging to pay on demand or at a fixed or determinable future time a sum certain in money, to order or to bearer.',
            },
            {
                value: '68',
                label: 'Online payment service',
                text: 'Payment will be made or has been made by an online payment service.',
            },
            {
                value: '70',
                label: 'Bill drawn by the creditor on the debtor',
                text: 'Bill drawn by the creditor on the debtor.',
            },
            {
                value: '74',
                label: 'Bill drawn by the creditor on a bank',
                text: 'Bill drawn by the creditor on a bank.',
            },
            {
                value: '75',
                label: 'Bill drawn by the creditor, endorsed by another bank',
                text: 'Bill drawn by the creditor, endorsed by another bank.',
            },
            {
                value: '76',
                label: 'Bill drawn by the creditor on a bank and endorsed by a third party',
                text: 'Bill drawn by the creditor on a bank and endorsed by a third party.',
            },
            {
                value: '77',
                label: 'Bill drawn by the creditor on a third party',
                text: 'Bill drawn by the creditor on a third party.',
            },
            {
                value: '78',
                label: 'Bill drawn by creditor on third party, accepted and endorsed by bank',
                text: 'Bill drawn by creditor on third party, accepted and endorsed by bank.',
            },
            {
                value: '91',
                label: "Not transferable banker's draft",
                text: 'Issue a bankers draft not endorsable.',
            },
            {
                value: '92',
                label: 'Not transferable local cheque',
                text: 'Issue a cheque not endorsable in payment of the funds.',
            },
            {
                value: '93',
                label: 'Reference giro',
                text:
                    "Ordering customer tells the bank to use the payment system 'Reference giro'. Used in the Finnish national banking system.",
            },
            {
                value: '94',
                label: 'Urgent giro',
                text:
                    "Ordering customer tells the bank to use the bank service 'Urgent Giro' when transferring the payment. Used in Finnish national banking system.",
            },
            {
                value: '95',
                label: 'Free format giro',
                text:
                    "Ordering customer tells the ordering bank to use the bank service 'Free Format Giro' when transferring the payment. Used in Finnish national banking system.",
            },
            {
                value: '96',
                label: 'Requested method for payment was not used',
                text:
                    'If the requested method for payment was or could not be used, this code indicates that.',
            },
            {
                value: '97',
                label: 'Clearing between partners',
                text:
                    'Amounts which two partners owe to each other to be compensated in order to avoid useless payments.',
            },
            {
                value: '98',
                label: 'JP, Electronically Recorded Monetary Claims',
                text:
                    'An electronically recorded monetary claim is a claim that is separate from the underlying debt that gave rise to its accrual. Therefore, even if an electronically recorded monetary claim is accrued as a means of payment of the underlying debt, the underlying debt will not be extinguished as a matter of course.',
            },
            {
                value: 'ZZZ',
                label: 'Mutually defined',
                text:
                    'A code assigned within a code list to be used on an interim basis and as defined among trading partners until a precise code can be assigned to the code list.',
            },
            {
                value: '69',
                label: 'Transfer Advice',
                text: '',
            },
        ];
    },
);

export const handleUpdateInvoiceBody = domain.createEvent();

export const $doesInvoiceExist = $invoiceState
    .map((state) => {
        if (state === null || (typeof state === 'string' && state === 'new')) {
            return false;
        }
        return true;
    })
    .reset(CreateInvoiceGate.close);

function isAllowedToEdit(params: {
    status: DocumentStatus;
    side: 'OUTGOING' | 'INCOMING';
    permissions: UserPermissionValue[];
}) {
    return {
        toRead: checkPermission(params.status, params.side, params.permissions, 'read'),
        toEdit: checkPermission(params.status, params.side, params.permissions, 'edit'),
        toDelete: checkPermission(params.status, params.side, params.permissions, 'delete'),
        toDownload: checkPermission(params.status, params.side, params.permissions, 'download'),
        toSendToApproval: checkPermission(
            params.status,
            params.side,
            params.permissions,
            'sendToApproval',
        ),
        toReturn: checkPermission(params.status, params.side, params.permissions, 'return'),
        toApprove: checkPermission(params.status, params.side, params.permissions, 'approve'),
        toSendToBuyer: checkPermission(
            params.status,
            params.side,
            params.permissions,
            'sendToBuyer',
        ),
        toCheckSuccess: checkPermission(
            params.status,
            params.side,
            params.permissions,
            'checkSuccess',
        ),
        toCheckFail: checkPermission(params.status, params.side, params.permissions, 'checkFail'),
        toAccept: checkPermission(params.status, params.side, params.permissions, 'accept'),
        toReject: checkPermission(params.status, params.side, params.permissions, 'reject'),
    };
}

export type AllowedActionsForInvoice = {
    read: boolean;
    edit: boolean;
    delete: boolean;
    sendToApproval: boolean;
    approve: boolean;
    download: boolean;
    return: boolean;
    sendToBuyer: boolean;
    checkSuccess: boolean;
    checkFail: boolean;
    accept: boolean;
    reject: boolean;
};
export const $allowedActionsForInvoice: Store<AllowedActionsForInvoice> = combine(
    {statusInfo: $invoiceStatusInfo, permissions: permissions$, $invoiceState},
    (params) => {
        const {statusInfo, permissions} = params;

        const {status, side} = statusInfo ?? {};

        if (!status || !side || !permissions.length) {
            return {
                read: false,
                edit: false,
                delete: false,
                sendToApproval: false,
                approve: false,
                download: false,
                return: false,
                sendToBuyer: false,
                checkSuccess: false,
                checkFail: false,
                accept: false,
                reject: false,
            };
        }

        const {
            toRead,
            toEdit,
            toDelete,
            toSendToApproval,
            toDownload,
            toReturn,
            toApprove,
            toSendToBuyer,
            toCheckSuccess,
            toCheckFail,
            toAccept,
            toReject,
        } = isAllowedToEdit({
            status,
            side,
            permissions,
        });

        const allowances = {
            read: toRead,
            edit: toEdit,
            delete: toDelete,
            sendToApproval: toSendToApproval,
            download: toDownload,
            return: toReturn,
            approve: toApprove,
            sendToBuyer: toSendToBuyer,
            checkSuccess: toCheckSuccess,
            checkFail: toCheckFail,
            accept: toAccept,
            reject: toReject,
        };

        return allowances;
    },
).reset(CreateInvoiceGate.close);

export type SubmitTaxesFormPayload = {
    allowToSend?: boolean;
};
export const submitTaxesFormRequested = domain.createEvent<SubmitTaxesFormPayload>();

export const setTargetStatus = domain.createEvent<DocumentStatus | null | 'DELETE'>();
export const $targetStatus = restore<DocumentStatus | null | 'DELETE'>(
    setTargetStatus,
    null,
).reset([CreateInvoiceGate.close]);

export const $blockNavigationFromCommon = combine(
    {$newInvoiceIsDone, $allowedActionsForInvoice, $targetStatus, $fetchDataFxFailed},
    (params) => {
        const {
            $newInvoiceIsDone: newInvoiceIsDone,
            $allowedActionsForInvoice: allowedActionsForInvoice,
            $targetStatus: targetStatus,
            $fetchDataFxFailed: fetchDataFxFailed,
        } = params;
        return (
            !newInvoiceIsDone &&
            allowedActionsForInvoice.edit &&
            targetStatus !== 'DELETE' &&
            !fetchDataFxFailed
        );
    },
);

export const $invoiceDeletingPending = combine(deleteInvoiceFx.pending, (pending) => pending).reset(
    CreateInvoiceGate.close,
);
